var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useState, useEffect, useRef, useCallback, useMemo } from 'preact/hooks';
import { fetchColourwayProductsByIds, } from '../platforms/shopify/fetchColourwayProducts';
import { relatedColourwayStorefrontIds } from './relatedColourways';
const DESKTOP_MIN_WIDTH = 769;
function useIsDesktop() {
    const [isDesktop, setIsDesktop] = useState(() => {
        if (typeof window === 'undefined')
            return false;
        return window.matchMedia(`(min-width: ${DESKTOP_MIN_WIDTH}px)`).matches;
    });
    useEffect(() => {
        const mq = window.matchMedia(`(min-width: ${DESKTOP_MIN_WIDTH}px)`);
        const onChange = () => setIsDesktop(mq.matches);
        mq.addEventListener('change', onChange);
        return () => mq.removeEventListener('change', onChange);
    }, []);
    return isDesktop;
}
/**
 * Lazy-loads related-colourway Storefront data for any products that become
 * visible on screen. Shared by `ProductGrid` and the Boost recommendations
 * carousel so both surfaces get the same hover/swatch behavior on ProductCard.
 *
 * Usage:
 *   const { colourwayData, observeCard } = useColourwayStorefrontData(products, { enabled: !loading });
 *   // For each rendered card:
 *   <div ref={el => observeCard(String(p.id), el)} data-product-id={p.id}>...</div>
 *   <ProductCard product={p} colourwayStorefrontData={colourwayData} />
 *
 * Only active on desktop (matches ProductGrid's original gating — mobile doesn't
 * show hover media, so the Storefront round-trip isn't worth it).
 */
export function useColourwayStorefrontData(products, { enabled = true } = {}) {
    const isDesktop = useIsDesktop();
    // const active = enabled && isDesktop;
    const active = enabled;
    const observerRef = useRef(null);
    const elMapRef = useRef(new Map());
    const visibleIdsRef = useRef(new Set());
    const fetchedGidsRef = useRef(new Set());
    const [visibleProductIds, setVisibleProductIds] = useState(() => new Set());
    const [colourwayData, setColourwayData] = useState(() => new Map());
    const observeCard = useCallback((productId, el) => {
        const obs = observerRef.current;
        const prev = elMapRef.current.get(productId);
        if (prev) {
            obs === null || obs === void 0 ? void 0 : obs.unobserve(prev);
            elMapRef.current.delete(productId);
        }
        if (el) {
            elMapRef.current.set(productId, el);
            obs === null || obs === void 0 ? void 0 : obs.observe(el);
        }
    }, []);
    useEffect(() => {
        var _a;
        if (!active) {
            (_a = observerRef.current) === null || _a === void 0 ? void 0 : _a.disconnect();
            observerRef.current = null;
            visibleIdsRef.current.clear();
            setVisibleProductIds(new Set());
            return undefined;
        }
        const obs = new IntersectionObserver(entries => {
            let changed = false;
            for (const entry of entries) {
                const pid = entry.target.getAttribute('data-product-id');
                if (!pid)
                    continue;
                if (entry.isIntersecting) {
                    if (!visibleIdsRef.current.has(pid)) {
                        visibleIdsRef.current.add(pid);
                        changed = true;
                    }
                }
                else if (visibleIdsRef.current.delete(pid)) {
                    changed = true;
                }
            }
            if (changed)
                setVisibleProductIds(new Set(visibleIdsRef.current));
        }, { threshold: 0.01, root: null, rootMargin: '0px' });
        observerRef.current = obs;
        elMapRef.current.forEach(el => obs.observe(el));
        return () => {
            obs.disconnect();
            observerRef.current = null;
        };
    }, [active]);
    const idsToFetch = useMemo(() => {
        const set = new Set();
        for (const p of products) {
            if (!visibleProductIds.has(String(p.id)))
                continue;
            for (const gid of relatedColourwayStorefrontIds(p)) {
                set.add(gid);
            }
        }
        return Array.from(set).sort();
    }, [products, visibleProductIds]);
    const idsKey = idsToFetch.join('|');
    useEffect(() => {
        if (!active || idsToFetch.length === 0)
            return undefined;
        const missing = idsToFetch.filter(id => !fetchedGidsRef.current.has(id));
        if (missing.length === 0)
            return undefined;
        const timer = window.setTimeout(() => {
            void (() => __awaiter(this, void 0, void 0, function* () {
                const data = yield fetchColourwayProductsByIds(missing);
                data.forEach((_, gid) => fetchedGidsRef.current.add(gid));
                setColourwayData(prev => {
                    const next = new Map(prev);
                    data.forEach((v, k) => next.set(k, v));
                    return next;
                });
            }))();
        }, 200);
        return () => window.clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active, idsKey]);
    return { colourwayData, observeCard };
}
